import React, { useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';
import styles from './CollapsibleSection.module.css';

/**
 * CollapsibleSection component
 *
 * Props:
 * - header: ReactNode (required) - header content (shown always)
 * - children: ReactNode (required) - collapsible content
 * - expanded?: boolean (optional, controlled mode)
 * - defaultExpanded?: boolean (optional, uncontrolled mode)
 * - onToggle?: (expanded: boolean) => void (optional)
 * - className?: string
 * - style?: React.CSSProperties
 */
const CollapsibleSection = ({
  header,
  children,
  expanded,
  defaultExpanded = false,
  onToggle,
  className = '',
  style = {},
}) => {
  const isControlled = expanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isOpen = isControlled ? expanded : internalExpanded;

  const handleToggle = () => {
    if (!isControlled) setInternalExpanded(e => !e);
    if (onToggle) onToggle(!isOpen);
  };

  return (
    <div className={`${styles.collapsibleSection} ${className}`} style={style}>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        className={styles.headerButton}
      >
        <span className={styles.headerContent}>{header}</span>
        <span className={styles.icon}>
          {isOpen ? (
            <ExpandLessIcon sx={{ width: 16, height: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ width: 16, height: 16 }} />
          )}
        </span>
      </button>
      {isOpen && <div className={styles.content}>{children}</div>}
    </div>
  );
};

CollapsibleSection.propTypes = {
  header: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  expanded: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default CollapsibleSection;
